import React, { useMemo } from "react";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Stack } from "@mui/material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom-v5-compat";

import { TransitionDetailsDTO } from "@fartherfinance/frontend/api/TransitionWorkflows/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import DetailsSection from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/TransitionDetails/components/DetailsSection/DetailsSection";
import TransitionStatusChip from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/TransitionStatusChip/TransitionStatusChip";
import {
  famOnboardingStatusMap,
  formatPercentage,
  formatTransitionDate,
  joinAdvisorNames,
  selectFirstTransitionAdvisor,
  transitionPhaseMap,
} from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/utils";
import Text from "@src/sharedComponents/Typography/Text";
import toUSD from "@src/utils/toUSD";

import TransitionExpandableContent from "./components/ExpandableTableRow/TransitionExpandableContent";

import styles from "./TransitionsDetails.module.css";

interface TransitionDetailsProps {
  transitionDetails?: TransitionDetailsDTO;
  isLoading?: boolean;
  hasError?: boolean;
}

const TransitionsDetailsContent: React.FC<TransitionDetailsProps> = ({
  transitionDetails,
  isLoading,
  hasError,
}) => {
  const navigate = useNavigate();

  const handleOnboardingTrackerClick = () => {
    if (transitionDetails?.transitionId) {
      navigate(
        `/Advisor/Transitions/${transitionDetails.transitionId}/Summary`
      );
    }
  };

  const handleClientTrackerClick = () => {
    const clientTrackerUrl = transitionDetails?.clientTrackerUrl;
    if (clientTrackerUrl) {
      window.open(clientTrackerUrl, "_blank");
    }
  };

  const handleRedirectToTask = () => {
    if (transitionDetails?.taskId) {
      navigate(`/Advisor/Tasks/${transitionDetails.taskId}`);
    }
  };

  const transitionOverview = useMemo(
    () => [
      {
        label: "Launch Date",
        value: transitionDetails?.task?.dueDate ? (
          formatTransitionDate(transitionDetails.task.dueDate)
        ) : (
          <Text variant="body" color="textDisabled">
            --
          </Text>
        ),
      },
      {
        label: "Advisor Team",
        value: transitionDetails?.team || (
          <Text variant="body" color="textDisabled">
            --
          </Text>
        ),
      },
      {
        label: "Advisors",
        value: transitionDetails?.advisors.length ? (
          joinAdvisorNames(transitionDetails.advisors)
        ) : (
          <Text variant="body" color="textDisabled">
            --
          </Text>
        ),
      },
      {
        label: "Email",
        value: selectFirstTransitionAdvisor(transitionDetails?.advisors ?? [])
          ?.emailAddress || (
          <Text variant="body" color="textDisabled">
            --
          </Text>
        ),
      },
      /*{
        label: "Phone Number",
        value: (
          <Text variant="body" color="textDisabled">
            --
          </Text>
        ),
      }*/
      {
        label: "Transition Status",
        value: (
          <TransitionStatusChip
            status={transitionDetails?.transitionStatus ?? "OnTrack"}
            variant="transition"
          />
        ),
      },
      {
        label: "Onboarding Status",
        value: (
          <TransitionStatusChip
            status={transitionDetails?.onboardingStatus ?? "InRecruiting"}
            variant="onboarding"
          />
        ),
      },
      {
        label: "Sentiment",
        value: transitionDetails?.sentiment ? (
          <TransitionStatusChip
            status={transitionDetails.sentiment}
            variant="sentiment"
          />
        ) : (
          <Text variant="body" color="textDisabled">
            --
          </Text>
        ),
      },
    ],
    [transitionDetails]
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Text variant="body">Loading transition details...</Text>
      </div>
    );
  }

  if (hasError || !transitionDetails) {
    return (
      <div className={styles.container}>
        <Text variant="body">Error loading transition details</Text>
      </div>
    );
  }

  // Points of Contact
  const pointOfContact = [
    {
      label: "CX Team Lead",
      value: transitionDetails.cxTeamLead || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Onboarding Manager",
      value: transitionDetails.onboardingManager || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Marketing Owner",
      value: transitionDetails.marketingOwner || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Executive Sponsor",
      value: transitionDetails.executiveSponsor || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
  ];

  // Transition Details
  const transitionDetailsData = [
    {
      label: "Registration Status",
      value: (
        <TransitionStatusChip
          status={transitionDetails.registrationStatus ?? "NotRegistered"}
          variant="registration"
        />
      ),
    },
    {
      label: "ADV-W Deadline",
      value: transitionDetails.advWDeadline ? (
        formatTransitionDate(new Date(transitionDetails.advWDeadline))
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Transition Type",
      value: transitionDetails.transitionType || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Custodian",
      value: transitionDetails.custodian || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Custodian Rep Code",
      value: transitionDetails.custodianRepCode || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Prior Firm",
      value: transitionDetails.priorFirm || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Additional Employees",
      value: transitionDetails.additionalEmployees?.toString() || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Transition Phase",
      value: transitionDetails.transitionPhase ? (
        transitionPhaseMap[transitionDetails.transitionPhase]
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Households",
      value: transitionDetails.households?.toString() || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Documents Sent",
      value: transitionDetails.docsSentPercent ? (
        formatPercentage(transitionDetails.docsSentPercent)
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Documents Signed",
      value: transitionDetails.docsSignedPercent ? (
        formatPercentage(transitionDetails.docsSignedPercent)
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Portals Created",
      value: transitionDetails.portalsCreatedPercent ? (
        formatPercentage(transitionDetails.portalsCreatedPercent)
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Billing",
      value: transitionDetails.billing || (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "BD Bulk Request",
      value: transitionDetails.bdBulkRequest ? (
        format(new Date(transitionDetails.bdBulkRequest), "MM/dd/yyyy")
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "FAM Onboarding Status",
      value: transitionDetails.famOnboardingStatus ? (
        famOnboardingStatusMap[transitionDetails.famOnboardingStatus]
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Expected AUM",
      value: transitionDetails.expectedAum ? (
        toUSD(transitionDetails.expectedAum, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
    {
      label: "Transitioned AUM",
      value: transitionDetails.transitionedAum ? (
        toUSD(transitionDetails.transitionedAum, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      ) : (
        <Text variant="body" color="textDisabled">
          --
        </Text>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text
          variant="title2"
          className={styles.title}
          onClick={handleRedirectToTask}
        >
          {joinAdvisorNames(transitionDetails.advisors)}
          <KeyboardArrowRightIcon className={styles.icon} />
        </Text>
        <Text variant="body" color="textSubtle">
          {transitionDetails.team}
        </Text>
      </div>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          buttonType="primary"
          text="Onboarding Tracker"
          endIcon={<KeyboardArrowRightIcon />}
          onClick={handleOnboardingTrackerClick}
          className={styles.detailsButton}
        />
        <Button
          variant="outlined"
          buttonType="primary"
          text="Client Tracker"
          endIcon={<OpenInNewIcon />}
          onClick={handleClientTrackerClick}
          className={styles.trackerButton}
        />
      </Stack>

      <DetailsSection
        title="Transition Overview"
        data={transitionOverview}
        titleVariant="title1"
      />

      <TransitionExpandableContent transitionDetails={transitionDetails} />

      <DetailsSection title="Points of Contact" data={pointOfContact} />

      <DetailsSection title="Transition Details" data={transitionDetailsData} />
    </div>
  );
};

export default TransitionsDetailsContent;
